import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  User, Building2, Phone, Mail, MapPin, Lock, Globe,
  Save, Eye, EyeOff, AlertCircle, Check, Loader2, ArrowLeft,
  Upload, Image, Camera, Crop, X, Crown
} from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminProfile = ({ onBack }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [loadingPincode, setLoadingPincode] = useState(false);
  
  // Logo state
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [crop, setCrop] = useState({ unit: '%', width: 50, aspect: 1 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Profile form state
  const [profileForm, setProfileForm] = useState({
    // Editable fields
    brand_name: '',
    gstin: '',
    pincode: '',
    country: '',
    state: '',
    district: '',
    city: '',
    company_legal_address: '',
    
    // Non-editable fields (display only, but allow initial fill if empty)
    owner_name: '',
    company_name: '',
    contact_phone: '',
    contact_email: ''
  });
  
  // Track which non-editable fields were originally empty
  const [originallyEmpty, setOriginallyEmpty] = useState({
    owner_name: false,
    company_name: false,
    contact_phone: false,
    contact_email: false
  });
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/client/profile`, {
        headers: getAuthHeaders()
      });
      
      const data = await response.json();
      
      if (data.success) {
        setProfile(data.client);
        
        const client = data.client;
        const bp = client.business_profile || {};
        
        // Helper to get value from business_profile first, then root level
        const getValue = (bpField, ...rootFields) => {
          if (bp[bpField]) return bp[bpField];
          for (const field of rootFields) {
            if (client[field]) return client[field];
          }
          return '';
        };
        
        // Populate form
        const formData = {
          brand_name: getValue('brand_name', 'brand_name'),
          gstin: getValue('gstin', 'gst_number', 'gstin'),
          pincode: getValue('pincode', 'pincode'),
          country: getValue('country', 'country') || '',
          state: getValue('state', 'state'),
          district: getValue('district', 'district'),
          city: getValue('city', 'city'),
          company_legal_address: getValue('company_legal_address', 'address', 'company_legal_address'),
          owner_name: getValue('owner_name', 'owner_name', 'contact_name'),
          company_name: getValue('company_name', 'company_name', 'business_name'),
          contact_phone: getValue('contact_phone', 'phone', 'contact_phone'),
          contact_email: getValue('contact_email', 'email', 'contact_email')
        };
        
        setProfileForm(formData);
        
        // Track which non-editable fields were originally empty
        setOriginallyEmpty({
          owner_name: !formData.owner_name,
          company_name: !formData.company_name,
          contact_phone: !formData.contact_phone,
          contact_email: !formData.contact_email
        });
        
        // Set logo preview
        const logoUrl = getValue('logo_url', 'logo_url');
        if (logoUrl) {
          setLogoPreview(logoUrl.startsWith('http') ? logoUrl : `${API_URL}${logoUrl}`);
        }
      } else {
        toast.error(data.error || 'Failed to load profile');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  // Pincode lookup
  const handlePincodeChange = async (pincode) => {
    setProfileForm(prev => ({ ...prev, pincode }));
    
    if (pincode.length === 6) {
      setLoadingPincode(true);
      try {
        const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
        const data = await response.json();
        
        if (data[0]?.Status === 'Success' && data[0]?.PostOffice?.length > 0) {
          const po = data[0].PostOffice[0];
          setProfileForm(prev => ({
            ...prev,
            country: po.Country || 'India',
            state: po.State || '',
            district: po.District || '',
            city: prev.city || po.Block || po.Name || ''
          }));
          toast.success('Location auto-filled from pincode');
        }
      } catch (error) {
        console.error('Pincode lookup error:', error);
      } finally {
        setLoadingPincode(false);
      }
    }
  };

  // Logo handling
  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo file size must be less than 5MB');
      return;
    }
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
  };

  const onImageLoad = useCallback((e) => {
    imgRef.current = e.currentTarget;
    const { width, height } = e.currentTarget;
    const size = Math.min(width, height);
    const x = (width - size) / 2;
    const y = (height - size) / 2;
    setCrop({
      unit: 'px',
      width: size * 0.8,
      height: size * 0.8,
      x: x + size * 0.1,
      y: y + size * 0.1
    });
  }, []);

  const getCroppedImg = async () => {
    if (!imgRef.current || !completedCrop) return null;
    
    const canvas = document.createElement('canvas');
    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
    
    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(
      imgRef.current,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );
    
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.9);
    });
  };

  const handleCropComplete = async () => {
    const croppedBlob = await getCroppedImg();
    if (croppedBlob) {
      setLogoFile(croppedBlob);
      setLogoPreview(URL.createObjectURL(croppedBlob));
    }
    setShowCropModal(false);
    setCropImageSrc(null);
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setCropImageSrc(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadLogo = async () => {
    if (!logoFile) return null;
    
    const formData = new FormData();
    formData.append('logo', logoFile, logoFile.name || 'logo.jpg');
    
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    const response = await fetch(`${API_URL}/api/onboarding/upload-logo`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || errorData.error || 'Failed to upload logo');
    }
    
    const data = await response.json();
    if (data.success) {
      return data.logo_url;
    }
    throw new Error(data.error || 'Failed to upload logo');
  };

  const handleProfileUpdate = async () => {
    setSaving(true);
    try {
      let logoUrl = null;
      
      // Upload logo if changed
      if (logoFile) {
        logoUrl = await uploadLogo();
      }
      
      // Build update payload - only include non-empty values
      const updatePayload = {};
      
      // Helper to add field only if it has a value
      const addIfNotEmpty = (key, value) => {
        if (value && value.trim && value.trim() !== '') {
          updatePayload[key] = value.trim();
        } else if (value && typeof value !== 'string') {
          updatePayload[key] = value;
        }
      };
      
      addIfNotEmpty('brand_name', profileForm.brand_name);
      addIfNotEmpty('gstin', profileForm.gstin);
      addIfNotEmpty('pincode', profileForm.pincode);
      addIfNotEmpty('country', profileForm.country);
      addIfNotEmpty('state', profileForm.state);
      addIfNotEmpty('district', profileForm.district);
      addIfNotEmpty('city', profileForm.city);
      addIfNotEmpty('company_legal_address', profileForm.company_legal_address);
      
      if (logoUrl) {
        updatePayload.logo_url = logoUrl;
      }
      
      // Include non-editable fields only if they were originally empty
      if (originallyEmpty.owner_name && profileForm.owner_name) {
        addIfNotEmpty('owner_name', profileForm.owner_name);
      }
      if (originallyEmpty.company_name && profileForm.company_name) {
        addIfNotEmpty('company_name', profileForm.company_name);
      }
      if (originallyEmpty.contact_phone && profileForm.contact_phone) {
        addIfNotEmpty('contact_phone', profileForm.contact_phone);
      }
      if (originallyEmpty.contact_email && profileForm.contact_email) {
        addIfNotEmpty('contact_email', profileForm.contact_email);
      }
      
      console.log('Profile update payload:', updatePayload);
      
      const response = await fetch(`${API_URL}/api/client/profile`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updatePayload)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        toast.error(data.detail || data.error || 'Failed to update profile');
        return;
      }
      
      if (data.success) {
        toast.success('Profile updated successfully!');
        setLogoFile(null);
        fetchProfile(); // Refresh data
      } else {
        toast.error(data.error || data.detail || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!passwordForm.current_password) {
      toast.error('Please enter your current password');
      return;
    }
    
    if (passwordForm.new_password.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    setChangingPassword(true);
    try {
      const response = await fetch(`${API_URL}/api/client/change-password`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Password changed successfully!');
        setPasswordForm({
          current_password: '',
          new_password: '',
          confirm_password: ''
        });
      } else {
        toast.error(data.error || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error('Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        {onBack && (
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
        )}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Business Profile</h1>
          <p className="text-gray-600">Manage your business information</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="profile" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Profile Details
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Lock className="w-4 h-4" />
            Security
          </TabsTrigger>
          <TabsTrigger value="language" className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            Language
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card>
            <CardContent className="pt-6 space-y-6">
              
              {/* Client ID - Top */}
              <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
                <Label className="text-xs text-amber-700 font-medium">Client ID</Label>
                <p className="font-mono text-xl font-bold text-amber-800 tracking-wide">
                  {profile?.client_id}
                </p>
              </div>

              {/* Logo Section */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Business Logo</Label>
                <div className="flex items-center gap-6">
                  <div className="relative">
                    {logoPreview ? (
                      <img 
                        src={logoPreview} 
                        alt="Logo" 
                        className="w-24 h-24 rounded-xl object-cover border-2 border-gray-200 shadow-sm"
                        onError={() => setLogoPreview(null)}
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-xl bg-gray-100 flex items-center justify-center border-2 border-dashed border-gray-300">
                        <Image className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoSelect}
                      className="hidden"
                    />
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      {logoPreview ? 'Change Logo' : 'Upload Logo'}
                    </Button>
                    <p className="text-xs text-gray-500">
                      Max 5MB. All image formats supported.
                    </p>
                  </div>
                </div>
              </div>

              {/* Non-editable Contact Info */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-4">
                <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Contact Information
                  <span className="text-xs text-gray-500 font-normal">(Cannot be changed after setup)</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="owner_name" className="text-sm">Owner / Contact Name</Label>
                    <Input
                      id="owner_name"
                      value={profileForm.owner_name}
                      onChange={(e) => originallyEmpty.owner_name && setProfileForm(prev => ({ ...prev, owner_name: e.target.value }))}
                      placeholder="Owner's full name"
                      disabled={!originallyEmpty.owner_name}
                      className={!originallyEmpty.owner_name ? 'bg-gray-100 cursor-not-allowed' : ''}
                    />
                  </div>
                  <div>
                    <Label htmlFor="company_name" className="text-sm">Company Name</Label>
                    <Input
                      id="company_name"
                      value={profileForm.company_name}
                      onChange={(e) => originallyEmpty.company_name && setProfileForm(prev => ({ ...prev, company_name: e.target.value }))}
                      placeholder="Company name"
                      disabled={!originallyEmpty.company_name}
                      className={!originallyEmpty.company_name ? 'bg-gray-100 cursor-not-allowed' : ''}
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_phone" className="text-sm">Contact Phone</Label>
                    <Input
                      id="contact_phone"
                      value={profileForm.contact_phone}
                      onChange={(e) => originallyEmpty.contact_phone && setProfileForm(prev => ({ ...prev, contact_phone: e.target.value }))}
                      placeholder="+91 9876543210"
                      disabled={!originallyEmpty.contact_phone}
                      className={!originallyEmpty.contact_phone ? 'bg-gray-100 cursor-not-allowed' : ''}
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_email" className="text-sm">Contact Email</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      value={profileForm.contact_email}
                      onChange={(e) => originallyEmpty.contact_email && setProfileForm(prev => ({ ...prev, contact_email: e.target.value }))}
                      placeholder="contact@company.com"
                      disabled={!originallyEmpty.contact_email}
                      className={!originallyEmpty.contact_email ? 'bg-gray-100 cursor-not-allowed' : ''}
                    />
                  </div>
                </div>
              </div>

              {/* Editable Business Details */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Business Details
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="brand_name">Brand Name</Label>
                    <Input
                      id="brand_name"
                      value={profileForm.brand_name}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, brand_name: e.target.value }))}
                      placeholder="Your brand/trade name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="gstin">GSTIN</Label>
                    <Input
                      id="gstin"
                      value={profileForm.gstin}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, gstin: e.target.value.toUpperCase() }))}
                      placeholder="22AAAAA0000A1Z5"
                      maxLength={15}
                    />
                  </div>
                </div>
              </div>

              {/* Address Section */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Address Details
                </h3>
                
                {/* Pincode - triggers auto-fill */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="pincode">Pincode *</Label>
                    <div className="relative">
                      <Input
                        id="pincode"
                        value={profileForm.pincode}
                        onChange={(e) => handlePincodeChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="560001"
                        maxLength={6}
                      />
                      {loadingPincode && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Auto-fills location</p>
                  </div>
                  <div>
                    <Label htmlFor="country">Country</Label>
                    <Input
                      id="country"
                      value={profileForm.country}
                      disabled
                      className="bg-gray-100 cursor-not-allowed"
                      placeholder="Auto-filled"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State</Label>
                    <Input
                      id="state"
                      value={profileForm.state}
                      disabled
                      className="bg-gray-100 cursor-not-allowed"
                      placeholder="Auto-filled"
                    />
                  </div>
                  <div>
                    <Label htmlFor="district">District</Label>
                    <Input
                      id="district"
                      value={profileForm.district}
                      disabled
                      className="bg-gray-100 cursor-not-allowed"
                      placeholder="Auto-filled"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">City / Town</Label>
                    <Input
                      id="city"
                      value={profileForm.city}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, city: e.target.value }))}
                      placeholder="City or town name"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="company_legal_address">Company Legal Address</Label>
                  <textarea
                    id="company_legal_address"
                    value={profileForm.company_legal_address}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, company_legal_address: e.target.value }))}
                    placeholder="Complete business address including building name, street, landmarks"
                    className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                </div>
              </div>

              {/* Subscription Info - Bottom */}
              {profile?.subscription && (
                <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Crown className="w-5 h-5 text-purple-600" />
                    <h4 className="font-semibold text-purple-800">Subscription Plan</h4>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Plan:</span>
                      <p className="font-semibold text-purple-700">{profile.subscription.plan_name || profile.subscription_plan || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Status:</span>
                      <p className="font-semibold text-green-600">{profile.subscription.status || 'Active'}</p>
                    </div>
                    {profile.subscription.expiry_date && (
                      <div>
                        <span className="text-gray-600">Valid Until:</span>
                        <p className="font-semibold text-gray-700">
                          {new Date(profile.subscription.expiry_date).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    {profile.subscription.is_forever && (
                      <div>
                        <span className="text-xs bg-gradient-to-r from-purple-500 to-pink-500 text-white px-2 py-1 rounded-full">
                          Lifetime Access
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Save Button */}
              <Button onClick={handleProfileUpdate} disabled={saving} className="w-full h-12 text-base">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving Changes...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5" />
                Change Password
              </CardTitle>
              <CardDescription>Update your account password</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Your password must be at least 8 characters long. We recommend using a mix of letters, numbers, and symbols.
                </AlertDescription>
              </Alert>

              <div>
                <Label htmlFor="current_password">Current Password</Label>
                <div className="relative">
                  <Input
                    id="current_password"
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))}
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="new_password">New Password</Label>
                <div className="relative">
                  <Input
                    id="new_password"
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {passwordForm.new_password && passwordForm.new_password.length < 8 && (
                  <p className="text-xs text-red-500 mt-1">Password must be at least 8 characters</p>
                )}
              </div>

              <div>
                <Label htmlFor="confirm_password">Confirm New Password</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                  placeholder="Confirm new password"
                />
                {passwordForm.confirm_password && passwordForm.new_password !== passwordForm.confirm_password && (
                  <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                )}
                {passwordForm.confirm_password && passwordForm.new_password === passwordForm.confirm_password && passwordForm.new_password.length >= 8 && (
                  <p className="text-xs text-green-500 mt-1 flex items-center gap-1">
                    <Check className="w-3 h-3" /> Passwords match
                  </p>
                )}
              </div>

              <Button 
                onClick={handlePasswordChange} 
                disabled={changingPassword || !passwordForm.current_password || passwordForm.new_password.length < 8 || passwordForm.new_password !== passwordForm.confirm_password}
                className="w-full"
              >
                {changingPassword ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Changing Password...
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Change Password
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Language Tab */}
        <TabsContent value="language">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Language Settings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Select your preferred language. All system UI text will be translated to the selected language. Data from your business (orders, names, etc.) will remain in the original language.
              </p>
              <LanguageSelector variant="full" />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Logo Crop Modal */}
      <Dialog open={showCropModal} onOpenChange={setShowCropModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crop className="w-5 h-5" />
              Crop Your Logo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Drag to adjust the crop area. Square images work best for logos.
            </p>
            {cropImageSrc && (
              <div className="flex justify-center bg-gray-100 rounded-lg p-4">
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={1}
                  circularCrop={false}
                >
                  <img
                    src={cropImageSrc}
                    onLoad={onImageLoad}
                    alt="Crop preview"
                    style={{ maxHeight: '400px' }}
                  />
                </ReactCrop>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={handleCropCancel}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleCropComplete}>
                <Check className="w-4 h-4 mr-2" />
                Apply Crop
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProfile;
